import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiPaths } from "../../../../service/apiPaths";
import ApiService from "../../../../service/ApiService";
import { IAddTestSeries } from "../../TestSeries.props";
import toast from "react-hot-toast";
import { Question } from "../../../LiveMcq/Components/AddLiveQuestion/types";


interface QuestionsModalState {
  isOpen: boolean;
  questions: Question[];
  configIndex: number;
  selectedQuestions: string[];
  subject: string;
  level: string;
  marks: number;
  currentPage: number;
  totalPages: number;
  totalDocs: number;
  hasNext: boolean;
  hasPrev: boolean;
  loading: boolean;
  searchTerm: string;
}

const useAddTestSeries = () => {
  const [formData, setFormData] = useState<IAddTestSeries>({
    testSeriesName: "",
    languageName: "",
    courseName: "",
    standard: "",
    config: [],
    duration: 0,
    amount: 0,
    isPaid: false, // Default to Free
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [courses, setCourses] = useState<any[]>([]);
  const [languages, setLanguages] = useState<any[]>([]);
  const [subjectName, setSubjectName] = useState<any[]>([]);
  const [standard, setStandard] = useState<any[]>([]);
  const navigate = useNavigate();

  const [questionsModal, setQuestionsModal] = useState<QuestionsModalState>({
    isOpen: false,
    questions: [],
    configIndex: -1,
    selectedQuestions: [],
    subject: '',
    level: '',
    marks: 0,
    currentPage: 1,
    totalPages: 1,
    totalDocs: 0,
    hasNext: false,
    hasPrev: false,
    loading: false,
    searchTerm: ''
  });

  useEffect(() => {
    fetchCoursesAndLanguages();
  }, []);

  const fetchCoursesAndLanguages = async () => {
    try {
      setLoading(true);
      const [
        coursesResponse,
        languagesResponse,
        subjectResponse,
        standardResponse,
      ] = await Promise.all([
        ApiService({ method: "GET", endpoint: apiPaths.getCourse }),
        ApiService({ method: "GET", endpoint: apiPaths.getlanguage }),
        ApiService({ method: "GET", endpoint: apiPaths.getSubject }),
        ApiService({ method: "GET", endpoint: apiPaths.getStandard }),
      ]);

      setCourses(coursesResponse);
      setLanguages(languagesResponse);
      setSubjectName(subjectResponse);
      setStandard(standardResponse);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to fetch data.");
    } finally {
      setLoading(false);
    }
  };

  const fetchQuestionsForConfig = async (
    subject: string,
    level: string,
    marks: number,
    configIndex: number,
    page: number = 1,
    searchTerm: string = '',
    preserveSelections: string[] = [] // Add this parameter
  ) => {
    try {
      // Set loading state for the modal
      setQuestionsModal(prev => ({ ...prev, loading: true }));

      const params: any = {
        subject,
        level,
        // Removed languageName filtering intentionally as requested
        courseName: formData.courseName,
        page: page,
        limit: 20
      };

      // Only add search parameter if searchTerm is not empty
      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      const response = await ApiService({
        method: "GET",
        endpoint: apiPaths.getAllQuestions,
        params: params
      });

      if (!response || !response.docs || response.docs.length === 0) {
        if (page === 1) {
          const message = searchTerm
            ? `No questions found matching "${searchTerm}" for ${subject} (${level})`
            : `No questions found for ${subject} (${level})`;
          toast.error(message);
          setQuestionsModal(prev => ({ ...prev, loading: false }));
          return;
        }
      }

      // Determine what selections to use
      let selectedQuestions: any[];
      if (preserveSelections.length > 0) {
        // Use explicitly passed selections (for search/navigation)
        selectedQuestions = preserveSelections;
      } else if (page === 1 && !questionsModal.isOpen) {
        // Initial load - use existing questions from config
        selectedQuestions = formData?.config[configIndex]?.questions ?? [];
      } else {
        // Fallback to current modal selections
        selectedQuestions = questionsModal.selectedQuestions || [];
      }

      setQuestionsModal(prev => ({
        ...prev,
        isOpen: true,
        questions: response.docs || [],
        configIndex,
        selectedQuestions,
        subject,
        level,
        marks,
        currentPage: response.currentPage || page,
        totalPages: response.totalPages || 1,
        totalDocs: response.totalDocs || 0,
        hasNext: response.hasNext || false,
        hasPrev: response.hasPrev || false,
        loading: false,
        searchTerm: searchTerm
      }));

    } catch (error: any) {
      console.error('Error fetching questions:', error);
      toast.error('Failed to fetch questions');
      setQuestionsModal(prev => ({ ...prev, loading: false }));
    }
  };

  const handlePageChange = async (page: number) => {
    if (questionsModal.isOpen) {
      // Preserve current selections during pagination
      const currentSelections = questionsModal.selectedQuestions;

      // First update the modal state to show the current selections are preserved
      setQuestionsModal(prev => ({
        ...prev,
        selectedQuestions: currentSelections,
        loading: true
      }));

      await fetchQuestionsForConfig(
        questionsModal.subject,
        questionsModal.level,
        questionsModal.marks,
        questionsModal.configIndex,
        page,
        questionsModal.searchTerm,
        currentSelections // Pass current selections explicitly
      );
    }
  };

  const handleSearchChange = async (searchTerm: string) => {
    if (questionsModal.isOpen) {
      // Preserve current selections during search
      const currentSelections = questionsModal.selectedQuestions;

      // First update the modal state to show the current selections are preserved
      setQuestionsModal(prev => ({
        ...prev,
        selectedQuestions: currentSelections,
        loading: true,
        searchTerm: searchTerm
      }));

      await fetchQuestionsForConfig(
        questionsModal.subject,
        questionsModal.level,
        questionsModal.marks,
        questionsModal.configIndex,
        1, // Always start from page 1 for new search
        searchTerm,
        currentSelections // Pass current selections explicitly
      );
    }
  };

  const handleCustomQuestionsConfirm = (selectedQuestions: string[]) => {
    setFormData((prevData) => {
      const updatedConfig = [...prevData.config];
      updatedConfig[questionsModal.configIndex] = {
        ...updatedConfig[questionsModal.configIndex],
        random: selectedQuestions.length === 0,
        questions: selectedQuestions,
      };
      return {
        ...prevData,
        config: updatedConfig,
      };
    });

    setQuestionsModal({
      isOpen: false,
      questions: [],
      configIndex: -1,
      selectedQuestions: [],
      subject: '',
      level: '',
      marks: 0,
      currentPage: 1,
      totalPages: 1,
      totalDocs: 0,
      hasNext: false,
      hasPrev: false,
      loading: false,
      searchTerm: ''
    });
  };

  const handleModalClose = () => {
    setQuestionsModal({
      isOpen: false,
      questions: [],
      configIndex: -1,
      selectedQuestions: [],
      subject: '',
      level: '',
      marks: 0,
      currentPage: 1,
      totalPages: 1,
      totalDocs: 0,
      hasNext: false,
      hasPrev: false,
      loading: false,
      searchTerm: ''
    });
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { id, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [id]: value, // Value can be string or boolean, React handles it
    }));
  };

  const handleConfigChange = (e: any, index: any, field: any) => {
    const { name, value } = e.target;
    setFormData((prevData) => {
      const updatedConfig = [...prevData.config];
      updatedConfig[index] = {
        ...updatedConfig[index],
        [field]: value,
      };
      return {
        ...prevData,
        config: updatedConfig,
      };
    });
  };

  const handleAddConfigField = () => {
    setFormData((prevState) => ({
      ...prevState,
      config: [
        ...prevState.config,
        { subject: "", level: "", marks: 0, random: true },
      ],
    }));
  };

  const handleRemoveConfigField = (index: any) => {
    setFormData((prevState) => ({
      ...prevState,
      config: prevState.config.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async () => {
    try {
      const response = await ApiService({
        method: "POST",
        endpoint: apiPaths.createTestSeries,
        data: formData,
      });

      if (response) {
        toast.success("TestSeries added successfully");
        window.location.href = "/test-series";
      }
    } catch (error: any) {
      console.log(error);
      if (error.response?.status === 400) {
        if (error.response.data.message) {
          toast.error(error.response.data.message);
        }
      } else {
        toast.error("Error Occured");
      }
      console.error(error);
    }
  };

  const handleCancel = () => {
    window.location.href = "/test-series";
  };

  return {
    loading,
    formData,
    courses,
    languages,
    subjectName,
    standard,
    questionsModal,
    handleChange,
    handleConfigChange,
    handleAddConfigField,
    handleRemoveConfigField,
    handleSubmit,
    handleCancel,
    fetchQuestionsForConfig,
    handleCustomQuestionsConfirm,
    handleModalClose,
    handlePageChange,
    handleSearchChange
  };
};

export default useAddTestSeries;